import { useCallback, useEffect, useRef, useState } from 'react';
import { getMetaErrorMessage } from '../metaAdsConfig';
import { isMetaFase1Unavailable } from '../metaFase1Support';

/**
 * Una lectura de la fase 1 (desglose, métricas, conjuntos) con estado:
 * `idle` (deshabilitado) · `loading` · `ready` · `unavailable` · `error`.
 *
 * - `unavailable` (red / CORS, 404 sin `code`, 503 `META_ADS_NOT_MIGRATED`): «Todavía no disponible»,
 *   sin error rojo y sin ocultar nada más (`isMetaFase1Unavailable`).
 * - `error` (404 con `code`, 4xx / 5xx): mensaje + `reload`.
 * - Se vuelve a pedir cuando cambia `deps` (período, campaña…); la respuesta anterior se descarta.
 *
 * @param {(signal: AbortSignal) => Promise<object>} fetcher devuelve el envelope del servicio
 * @param {{ enabled?: boolean, deps?: any[] }} [options] `deps` identifica el pedido (strings / números)
 * @returns {{ status: string, res: object|null, error: string|null, reload: () => void }}
 */
export function useMetaRead(fetcher, { enabled = true, deps = [] } = {}) {
  const [state, setState] = useState({ status: enabled ? 'loading' : 'idle', res: null, error: null });
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const abortRef = useRef(null);
  const mountedRef = useRef(true);
  const key = JSON.stringify(deps);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      abortRef.current?.abort();
    };
  }, []);

  const run = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setState({ status: 'loading', res: null, error: null });
    let res;
    try {
      res = await fetcherRef.current(controller.signal);
    } catch (error) {
      // Cancelado (otro pedido lo reemplazó o se cerró el panel): no es un error.
      if (error?.name === 'AbortError') return;
      throw error;
    }
    if (!mountedRef.current || controller.signal.aborted) return;
    if (res?._httpOk) setState({ status: 'ready', res, error: null });
    else if (isMetaFase1Unavailable(res)) setState({ status: 'unavailable', res: null, error: null });
    else setState({ status: 'error', res: null, error: getMetaErrorMessage(res) });
  }, []);

  useEffect(() => {
    if (!enabled) {
      abortRef.current?.abort();
      setState({ status: 'idle', res: null, error: null });
      return;
    }
    run();
    // `key` resume `deps`: el pedido cambia solo cuando cambian sus valores.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, key, run]);

  return { ...state, reload: run };
}
